/**
 * RemittanceRow Component
 *
 * Dense row displaying an enriched remittance item.
 * Renders as a CSS Grid row in table layouts or as a stacked card on mobile.
 */

import React, { useCallback } from 'react';
import { Box, Checkbox, Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { RemittanceListItem } from '@store/generated/billing';

import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CHK: 'Check',
  ACH: 'ACH',
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '\u2014';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '\u2014';
  }
};

const formatCents = (cents: number | null | undefined): string => {
  if (cents == null) return '\u2014';
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RemittanceRowProps {
  remittance: RemittanceListItem;
  layout: TableLayoutMode;
  onRowClick?: (remittance: RemittanceListItem) => void;
  selected?: boolean;
  onSelectChange?: (remittanceId: string, selected: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RemittanceRow: React.FC<RemittanceRowProps> = ({
  remittance,
  layout,
  onRowClick,
  selected,
  onSelectChange,
}) => {
  const theme = useTheme();

  const handleRowClick = useCallback(() => {
    onRowClick?.(remittance);
  }, [remittance, onRowClick]);

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelectChange?.(remittance.remittance_id, !selected);
    },
    [remittance.remittance_id, selected, onSelectChange],
  );

  // Column visibility
  const showTrace = layout === 'full' || layout === 'standard';
  const showMethod = layout === 'full';
  const showDenials = layout === 'full' || layout === 'standard';

  const deniedCount = remittance.denied_claim_count ?? 0;
  const methodLabel = remittance.payment_method_code
    ? (PAYMENT_METHOD_LABELS[remittance.payment_method_code] ?? remittance.payment_method_code)
    : '\u2014';

  // ---- Card layout (mobile) ----

  if (layout === 'card') {
    return (
      <Box
        onClick={handleRowClick}
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
          cursor: onRowClick ? 'pointer' : 'default',
          bgcolor: selected ? `${theme.palette.primary.main}08` : 'transparent',
          transition: 'background-color 0.15s ease',
          '&:hover': onRowClick ? { bgcolor: theme.palette.grey[50] } : {},
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        {/* Top row: Payer + Amount */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          {onSelectChange && (
            <Checkbox checked={selected} onClick={handleCheckboxClick} size="small" sx={{ p: 0, flexShrink: 0, mr: 1 }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: 1 }}>
            {remittance.payer_name || 'Unknown Payer'}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600, flexShrink: 0 }}>
            {formatCents(remittance.total_payment_amount_cents)}
          </Typography>
        </Box>

        {/* Bottom row: Date + Method + Claims + Denials */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {formatDate(remittance.payment_date)}
          </Typography>
          <Chip label={methodLabel} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {remittance.claim_count ?? 0} claims
          </Typography>
          {deniedCount > 0 && (
            <Chip
              label={`${deniedCount} denied`}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                bgcolor: `${theme.palette.error.main}15`,
                color: theme.palette.error.main,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
        </Box>
      </Box>
    );
  }

  // ---- Table-row layout (full / standard / compact) ----

  return (
    <Box
      onClick={handleRowClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: getGridTemplateColumns(layout, !!onSelectChange),
        gap: GRID_GAP,
        alignItems: 'center',
        px: 2,
        py: 0.75,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: onRowClick ? 'pointer' : 'default',
        bgcolor: selected ? `${theme.palette.primary.main}08` : 'transparent',
        transition: 'background-color 0.15s ease',
        '&:hover': onRowClick ? { bgcolor: theme.palette.grey[50] } : {},
        '&:last-child': { borderBottom: 'none' },
        minHeight: 36,
      }}
    >
      {onSelectChange && (
        <Checkbox checked={selected} onClick={handleCheckboxClick} size="small" sx={{ p: 0, flexShrink: 0 }} />
      )}

      {/* Payment Date */}
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
        {formatDate(remittance.payment_date)}
      </Typography>

      {/* Payer */}
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.8rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={remittance.payer_name || undefined}
      >
        {remittance.payer_name || 'Unknown Payer'}
      </Typography>

      {/* Trace # */}
      {showTrace && (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={remittance.check_or_eft_trace_number}
        >
          {remittance.check_or_eft_trace_number}
        </Typography>
      )}

      {/* Method */}
      {showMethod && (
        <Box>
          <Chip
            label={methodLabel}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        </Box>
      )}

      {/* Amount */}
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          fontWeight: 500,
          textAlign: 'right',
        }}
      >
        {formatCents(remittance.total_payment_amount_cents)}
      </Typography>

      {/* Claims count */}
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.8rem',
          textAlign: 'right',
          color: theme.palette.text.secondary,
        }}
      >
        {remittance.claim_count ?? 0}
      </Typography>

      {/* Denials */}
      {showDenials && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {deniedCount > 0 ? (
            <Chip
              label={deniedCount}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                bgcolor: `${theme.palette.error.main}15`,
                color: theme.palette.error.main,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: theme.palette.text.disabled, textAlign: 'right' }}>
              0
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
